/**
 * The "new layout" tour (rules and copy: constants/layoutTour.js).
 *
 *   1. A card, once, on Home: where everything went, Got it or Show me.
 *   2. Show me: four stops on the real screen, each one the actual element
 *      (found by its data-tour-target) lit through a dimmed page, with a
 *      caption beside it. A stop whose element isn't on screen for this
 *      coach (no chats, say) is skipped rather than pointed at nothing.
 *   3. For a week after: a gold dot on each thing that moved, until it has
 *      been opened. Drawn by CSS from a body attribute (dashboard.css,
 *      "moved-pulse"), so the sidebar's own code doesn't have to know.
 *
 * State is one user_data row per coach, mirrored to localStorage so the card
 * never flashes up on a device that already knows it has been seen.
 */
import React, { useCallback, useEffect, useLayoutEffect, useState } from "react";
import { createPortal } from "react-dom";
import { GOLD } from "../constants/theme";
import { loadUserData, saveUserData } from "../utils/userData";
import { logEvent } from "../utils/analytics";
import {
  LAYOUT_TOUR_KEY, DESKTOP_STEPS, PHONE_STEPS,
  shouldShowCard, activePulses, openedBy,
} from "../constants/layoutTour";

const hf = "'Bebas Neue', sans-serif";

function readLocal() {
  try { const v = JSON.parse(localStorage.getItem(LAYOUT_TOUR_KEY) || "null"); return v && typeof v === "object" ? v : null; }
  catch { return null; }
}
function writeLocal(v) { try { localStorage.setItem(LAYOUT_TOUR_KEY, JSON.stringify(v)); } catch { /* private window */ } }
const CHANGED = "bam-layout-tour-changed";

/** The coach took the "What's here" tour, which shows the new layout, so the
 *  "We've made things a bit easier" card would be telling them twice. Marks
 *  it skipped (no card, no dots) unless they already had the card. */
export async function markLayoutTourSkipped(reason = "tour") {
  if (readLocal()?.seenAt) return;
  let remote = null;
  try { remote = await loadUserData(LAYOUT_TOUR_KEY, null); } catch { /* offline */ }
  if (remote && remote.seenAt) return;
  const next = { skipped: reason, at: new Date().toISOString() };
  writeLocal(next);
  try { window.dispatchEvent(new Event(CHANGED)); } catch { /* no window */ }
  Promise.resolve(saveUserData(LAYOUT_TOUR_KEY, next)).catch(() => {});
}

/** The visible element for a stop, or null. */
function findTarget(id) {
  if (typeof document === "undefined") return null;
  const els = document.querySelectorAll(`[data-tour-target="${id}"]`);
  for (const el of els) {
    const r = el.getBoundingClientRect();
    if (r.width > 0 && r.height > 0) return el;
  }
  return null;
}

const WHERE = [
  ["Chats and Calls", " sit at the bottom of the sidebar. On your phone, Chats is next to the bell."],
  ["My Portal", ", What's here and Submit Content are in the menu under your name."],
  ["Drills", " live in the Drill Library. Movement and Pro are collections inside it."],
  ["Learn", " has its own page: today's session, what's new, and where you left off."],
];
const WHERE_PHONE = [
  ["Chats", " is next to the bell, with your unread count."],
  ["The bar at the bottom", ": Home, Community, Learn, Drills, Coach."],
  ["My Portal", " is in the menu under You, with Calls just above it."],
  ["Learn", " opens today's session and where you left off."],
];

export function LayoutTour({ enabled, joinedAt, nav, isMobile, profileMenuOpen }) {
  const [state, setState] = useState(() => readLocal());
  const [loaded, setLoaded] = useState(false);
  const [phase, setPhase] = useState(null);     // "card" | "spot" | null
  const [step, setStep] = useState(0);
  const [rect, setRect] = useState(null);
  // Lets the one-time feature pop-ups (FeatureIntro) wait their turn.
  useEffect(() => {
    window.__bamLayoutTourOpen = !!phase;
    return () => { window.__bamLayoutTourOpen = false; };
  }, [phase]);

  const save = useCallback((next) => {
    setState(next);
    writeLocal(next);
    Promise.resolve(saveUserData(LAYOUT_TOUR_KEY, next)).catch(() => {});
  }, []);

  // Skipped from elsewhere (the "What's here" tour): pick it up now, so the
  // card can't open the moment that tour closes.
  useEffect(() => {
    const on = () => setState(readLocal());
    window.addEventListener(CHANGED, on);
    return () => window.removeEventListener(CHANGED, on);
  }, []);

  // The account's copy wins over this browser's, whichever has more in it.
  useEffect(() => {
    if (!enabled) return;
    let live = true;
    loadUserData(LAYOUT_TOUR_KEY, null).then(remote => {
      if (!live) return;
      if (remote && typeof remote === "object" && remote.skipped && !(readLocal() || {}).seenAt) {
        setState(remote); writeLocal(remote);
      } else if (remote && typeof remote === "object" && remote.seenAt) {
        const local = readLocal() || {};
        const merged = { ...remote, opened: Array.from(new Set([...(remote.opened || []), ...(local.opened || [])])) };
        setState(merged); writeLocal(merged);
      }
      setLoaded(true);
    }).catch(() => setLoaded(true));
    return () => { live = false; };
  }, [enabled]);

  // The card: once, on Home, for coaches who knew the old layout.
  useEffect(() => {
    if (!enabled || !loaded || phase || nav !== "dashboard") return;
    if (!shouldShowCard(joinedAt, state)) return;
    setPhase("card");
    save({ ...(state || {}), seenAt: new Date().toISOString(), opened: (state && state.opened) || [] });
    logEvent("layout_tour_shown", { mobile: !!isMobile });
  }, [enabled, loaded, phase, nav, joinedAt, state, save, isMobile]);

  // Opening a moved thing takes its dot away.
  useEffect(() => {
    const id = openedBy(nav) || (profileMenuOpen ? "profile" : null);
    if (!id || !state || !state.seenAt || (state.opened || []).includes(id)) return;
    save({ ...state, opened: [...(state.opened || []), id] });
  }, [nav, profileMenuOpen, state, save]);

  // The dots themselves are CSS, keyed off this attribute.
  const pulses = enabled && !phase ? activePulses(state) : [];
  const pulseKey = pulses.join(" ");
  useEffect(() => {
    if (typeof document === "undefined") return undefined;
    if (pulseKey) document.body.setAttribute("data-moved-pulse", pulseKey);
    else document.body.removeAttribute("data-moved-pulse");
    return () => document.body.removeAttribute("data-moved-pulse");
  }, [pulseKey]);

  // The stops this screen can actually show.
  const steps = (isMobile ? PHONE_STEPS : DESKTOP_STEPS);
  const current = phase === "spot" ? steps[step] : null;

  const finish = useCallback((how) => {
    logEvent("layout_tour_done", { how, step });
    setPhase(null); setStep(0); setRect(null);
  }, [step]);

  const advance = useCallback((from) => {
    let i = from;
    while (i < steps.length && !findTarget(steps[i].target)) i++;
    if (i >= steps.length) { finish("finished"); return; }
    setStep(i);
  }, [steps, finish]);

  // Measure the lit element, and keep measuring while the page moves.
  useLayoutEffect(() => {
    if (!current) return undefined;
    const el = findTarget(current.target);
    if (!el) { advance(step + 1); return undefined; }
    try { el.scrollIntoView({ block: "nearest" }); } catch { /* old browser */ }
    const measure = () => { const r = el.getBoundingClientRect(); setRect({ x: r.left, y: r.top, w: r.width, h: r.height }); };
    measure();
    window.addEventListener("resize", measure);
    window.addEventListener("scroll", measure, true);
    return () => { window.removeEventListener("resize", measure); window.removeEventListener("scroll", measure, true); };
  }, [current, step, advance]);

  useEffect(() => {
    if (!phase) return undefined;
    const onKey = (e) => { if (e.key === "Escape") finish("escape"); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [phase, finish]);

  if (!phase || typeof document === "undefined") return null;

  if (phase === "card") {
    const rows = isMobile ? WHERE_PHONE : WHERE;
    const gotIt = () => { logEvent("layout_tour_got_it", {}); setPhase(null); };
    const showMe = () => { logEvent("layout_tour_show_me", {}); setPhase("spot"); advance(0); };
    return createPortal(
      <div className="bam-layout-tour" style={{ position: "fixed", inset: 0, zIndex: 10050, background: "rgba(0,0,0,0.66)",
        display: "flex", alignItems: isMobile ? "flex-end" : "center", justifyContent: "center",
        animation: "bamSheetFade .2s ease-out" }}>
        <section role="dialog" aria-modal="true" aria-labelledby="layout-tour-title"
          style={{ width: isMobile ? "100%" : "min(720px, calc(100vw - 32px))", background: "#121212", color: "#F5F5F5",
            border: "1px solid rgba(255,255,255,0.08)", borderRadius: isMobile ? "18px 18px 0 0" : 20, overflow: "hidden",
            boxShadow: "0 30px 90px rgba(0,0,0,0.6)", fontFamily: "'DM Sans', sans-serif",
            paddingBottom: isMobile ? "env(safe-area-inset-bottom, 0px)" : 0,
            animation: isMobile ? "bamSheetUp .25s cubic-bezier(.32,.72,0,1)" : "bamSheetIn .22s cubic-bezier(.32,.72,0,1)" }}>
          <div style={{ padding: isMobile ? "18px 18px 4px" : "30px 32px 6px", display: "flex", flexDirection: "column", gap: 8 }}>
            <span style={{ fontSize: 11, fontWeight: 800, letterSpacing: 1.6, color: GOLD }}>NEW LAYOUT</span>
            <h1 id="layout-tour-title" style={{ margin: 0, fontFamily: hf, fontWeight: 400, fontSize: isMobile ? 34 : 44,
              letterSpacing: 1.2, lineHeight: .95 }}>We've made things a bit easier!</h1>
            {!isMobile && <p style={{ margin: 0, fontSize: 14.5, color: "#9CA3AF" }}>Five things down the side instead of fourteen. Here's where everything went.</p>}
          </div>
          <div style={{ display: "grid", gap: 24, padding: isMobile ? "12px 18px 16px" : "20px 32px 24px",
            gridTemplateColumns: isMobile ? "minmax(0,1fr)" : "210px minmax(0, 1fr)" }}>
            {!isMobile && (
              <div aria-hidden="true" style={{ borderRadius: 12, background: "#0A0A0A", border: "1px solid rgba(255,255,255,0.06)",
                padding: 12, display: "flex", flexDirection: "column", gap: 4, fontSize: 12.5, color: "#9CA3AF" }}>
                <span style={{ padding: "6px 8px" }}>Home</span>
                <span style={{ padding: "6px 8px" }}>Community</span>
                {["Learn", "Coach", "Drill Library"].map(l => (
                  <span key={l} style={{ padding: "6px 8px", borderRadius: 6, background: "rgba(226,221,159,0.12)", color: GOLD, fontWeight: 700 }}>{l}</span>
                ))}
                <span style={{ display: "flex", gap: 4, marginTop: 8 }}>
                  {["Calls", "Chats"].map(l => (
                    <span key={l} style={{ flex: 1, padding: "6px 8px", borderRadius: 6, border: `1px dashed ${GOLD}99`, color: GOLD, fontSize: 11, fontWeight: 700 }}>{l}</span>
                  ))}
                </span>
                <span style={{ marginTop: 4, padding: "6px 8px", borderRadius: 6, border: `1px dashed ${GOLD}99`, color: GOLD, fontSize: 11, fontWeight: 700 }}>Your name ▸ menu</span>
              </div>
            )}
            <div style={{ display: "flex", flexDirection: "column", gap: isMobile ? 11 : 14, fontSize: isMobile ? 13.5 : 14, lineHeight: 1.45 }}>
              {rows.map(([b, rest]) => (
                <div key={b} style={{ display: "flex", gap: 12 }}>
                  <span style={{ width: 8, height: 8, borderRadius: 4, background: GOLD, marginTop: 7, flexShrink: 0 }}/>
                  <span><b>{b}</b>{rest}</span>
                </div>
              ))}
            </div>
          </div>
          <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, padding: isMobile ? "4px 18px 22px" : "16px 32px 26px",
            borderTop: isMobile ? "none" : "1px solid rgba(255,255,255,0.06)" }}>
            <button type="button" className="btn" onClick={gotIt}
              style={{ flex: isMobile ? 1 : "none", minHeight: 46, padding: "0 22px", borderRadius: 23, cursor: "pointer",
                border: "1px solid rgba(255,255,255,0.2)", background: "transparent", color: "#F5F5F5",
                fontFamily: "inherit", fontSize: 14, fontWeight: 700 }}>Got it</button>
            <button type="button" className="btn" onClick={showMe} autoFocus
              style={{ flex: isMobile ? 1 : "none", minHeight: 46, padding: "0 22px", borderRadius: 23, cursor: "pointer",
                border: "none", background: GOLD, color: "#111", fontFamily: "inherit", fontSize: 14, fontWeight: 800 }}>Show me →</button>
          </div>
        </section>
      </div>,
      document.body
    );
  }

  // The spotlight.
  if (!current || !rect) return null;
  const pad = 6;
  const hole = { left: rect.x - pad, top: rect.y - pad, width: rect.w + pad * 2, height: rect.h + pad * 2 };
  const vw = window.innerWidth, vh = window.innerHeight;
  const cardW = Math.min(340, vw - 32);
  let pos;
  if (!isMobile && hole.left + hole.width + 20 + cardW < vw) {
    // Beside it, lined up with its middle, kept on screen.
    const top = Math.max(16, Math.min(vh - 230, hole.top + hole.height / 2 - 90));
    pos = { left: hole.left + hole.width + 18, top };
  } else if (hole.top + hole.height / 2 < vh / 2) {
    pos = { left: Math.max(16, Math.min(vw - cardW - 16, hole.left + hole.width / 2 - cardW / 2)), top: hole.top + hole.height + 14 };
  } else {
    pos = { left: Math.max(16, Math.min(vw - cardW - 16, hole.left + hole.width / 2 - cardW / 2)), bottom: vh - hole.top + 14 };
  }
  const shown = steps.filter(s => findTarget(s.target));
  const n = Math.max(1, shown.length);
  const at = Math.max(0, shown.findIndex(s => s.target === current.target));
  const last = at >= n - 1;

  return createPortal(
    <div className="bam-layout-tour" style={{ position: "fixed", inset: 0, zIndex: 10050 }} onClick={(e) => e.stopPropagation()}>
      <div aria-hidden="true" className="bam-tour-hole"
        style={{ position: "fixed", ...hole, borderRadius: 12, boxShadow: `0 0 0 2px ${GOLD}, 0 0 0 9999px rgba(0,0,0,0.72)`,
          transition: "left .3s cubic-bezier(.22,.8,.24,1), top .3s cubic-bezier(.22,.8,.24,1), width .3s cubic-bezier(.22,.8,.24,1), height .3s cubic-bezier(.22,.8,.24,1)" }}/>
      <section role="dialog" aria-modal="true" aria-labelledby="layout-spot-title" key={current.target}
        style={{ position: "fixed", ...pos, width: cardW, borderRadius: 16, background: "#121212", color: "#F5F5F5",
          border: `1px solid ${GOLD}59`, boxShadow: "0 20px 60px rgba(0,0,0,0.6)", padding: "18px 20px",
          display: "flex", flexDirection: "column", gap: 9, fontFamily: "'DM Sans', sans-serif",
          animation: "bamLearnIn .3s cubic-bezier(.22,.8,.24,1) both" }}>
        <span style={{ fontSize: 10.5, fontWeight: 800, letterSpacing: 1.6, color: GOLD }}>{at + 1} OF {n}</span>
        <span id="layout-spot-title" style={{ fontFamily: hf, fontSize: 29, lineHeight: 1 }}>{current.title}</span>
        <span style={{ fontSize: 13.5, color: "#C9C9C9", lineHeight: 1.5 }}>{current.body}</span>
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 4 }}>
          <span aria-hidden="true" style={{ display: "flex", gap: 5, flex: 1 }}>
            {shown.map((s, i) => (
              <span key={s.target} style={{ width: i === at ? 18 : 6, height: 4, borderRadius: 2,
                background: i <= at ? GOLD : "#444", transition: "width .25s ease" }}/>
            ))}
          </span>
          {!last && (
            <button type="button" className="btn" onClick={() => finish("skipped")}
              style={{ minHeight: 44, padding: "0 12px", borderRadius: 22, border: "none", background: "transparent", cursor: "pointer",
                color: "#9CA3AF", fontFamily: "inherit", fontSize: 13, fontWeight: 700 }}>Skip</button>
          )}
          <button type="button" className="btn" autoFocus onClick={() => (last ? finish("finished") : advance(step + 1))}
            style={{ minHeight: 44, padding: "0 18px", borderRadius: 22, border: "none", background: GOLD, color: "#111", cursor: "pointer",
              fontFamily: "inherit", fontSize: 13, fontWeight: 800 }}>{last ? "Done" : "Next →"}</button>
        </div>
      </section>
    </div>,
    document.body
  );
}

export default LayoutTour;
